import { useEffect, useState } from "react";
import axios from "axios";
import toast from "react-hot-toast";
import { jsPDF } from "jspdf";
import { useAuthStore } from "../../context/useAuthStore";
import { FaFileDownload } from "react-icons/fa";
import { MdAttachEmail } from "react-icons/md";
import myLogo from "../../assets/llb360.png";

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL;

type InvoiceDetail = {
  invoiceNumber: string;
  created: string;
  status?: string;
  amount: number;
  discount?: number;
  subjects?: string[];
};

type InvoiceResponseItem = {
  invoiceNumber: string;
  details: InvoiceDetail[];
};

type InvoiceItem = {
  subjectName: string;
  amount: number;
  status: string;
};

type InvoiceApiResponse = {
  userName: string;
  invoiceNumber: string;
  currency: string;
  date: string;
  items: InvoiceItem[];
  totalAmount: number;
  discount: number;
  email: string;
  logoURL: string;
};

type Invoice = {
  invoiceNumber: string;
  status: string;
  totalAmount: number;
  discount: number;
  subjects: { name: string; amount: number }[];
  date?: string;
  userName?: string;
  email?: string;
};

const tableHeaders = ["Invoice Id", "Date", "Status", "Amount", "Actions"];

const generatePdfContent = (doc: jsPDF, invoice: Invoice, user: any) => {
  const date = invoice.date
    ? new Date(invoice.date).toLocaleDateString("en-IN")
    : new Date().toLocaleDateString("en-IN");
  const pageHeight = doc.internal.pageSize.height;
  const margin = 10;

  doc.setFont("helvetica", "bold").setFontSize(20);
  doc.addImage(myLogo, "PNG", 10, 10, 34, 34);
  doc.text("Saadruso Internet Media Pvt Ltd", 45, 20);

  doc.setFontSize(10).setFont("helvetica", "normal");
  doc.text("28, 4th Phase, Doyens Colony,", 45, 26);
  doc.text("Serilingampalle (M), Telangana 500019", 45, 31);
  doc.text("GSTIN: 36ABJCS8550l1ZO", 45, 36);
  doc.text("Email: support@llb360.com | Ph: 7673937360", 45, 41);

  doc.setDrawColor(0).setLineWidth(0.3).line(10, 45, 200, 45);

  doc.setFontSize(16).setFont("helvetica", "bold");
  doc.text("TAX INVOICE", 105, 55, { align: "center" });

  const yStart = 65;
  doc.setFontSize(11).setFont("helvetica", "normal");
  doc.text("Billed From:", 14, yStart);
  doc.text(`Invoice No: ${invoice.invoiceNumber}`, 14, yStart + 6);
  doc.text(`Invoice Date: ${date}`, 14, yStart + 12);
  doc.text(`Payment Status: ${invoice.status}`, 14, yStart + 18);

  doc.text("Billed To:", 130, yStart);
  doc.text(
    `${invoice.userName || user?.fullName || "Customer Name"}`,
    130,
    yStart + 6
  );
  doc.text(
    `${invoice.email || user?.emailId || "customer@email.com"}`,
    130,
    yStart + 12
  );
  let phone = user?.phoneNumber || "0000000000";
  if (phone.startsWith("91")) phone = phone.slice(2);
  doc.text(phone, 130, yStart + 18);

  let currentY = yStart + 25;
  const rowHeight = 8;
  const renderTableHeader = () => {
    doc.setFillColor(230, 230, 230);
    doc.rect(10, currentY, 190, rowHeight, "F");
    doc.setFont("helvetica", "bold");
    doc.text("S.No", 15, currentY + 6);
    doc.text("Subject Name", 40, currentY + 6);
    doc.text("Amount", 160, currentY + 6);
    currentY += rowHeight + 2;
  };
  renderTableHeader();

  doc.setFont("helvetica", "normal");
  invoice.subjects.forEach((subject, index) => {
    if (currentY + rowHeight > pageHeight - 30) {
      doc.addPage();
      currentY = margin;
      renderTableHeader();
    }
    doc.text(`${index + 1}`, 15, currentY + 5);
    doc.text(subject.name, 40, currentY + 5);
    doc.text(subject.amount.toFixed(2), 170, currentY + 5, { align: "right" });
    currentY += rowHeight;
  });

  currentY += 10;
  doc.setFontSize(10).setTextColor(255, 0, 0);
  doc.text("Note: All the amounts are inclusive of GST.", 14, currentY);
  doc.setTextColor(0, 0, 0);

  currentY += 10;
  const tableX = 115,
    colWidth = 85,
    summaryRowHeight = 8;
  const total = invoice.subjects.reduce((sum, s) => sum + s.amount, 0);
  const discount = invoice.discount || 0;
  const amountPaid = total - discount;

  doc.setDrawColor(0).setFillColor(245, 245, 245);
  doc.rect(tableX, currentY, colWidth, summaryRowHeight * 5, "S");
  doc.setFont("helvetica", "bold");
  doc.setFillColor(230, 230, 230);
  doc.rect(tableX, currentY, colWidth, summaryRowHeight, "F");
  doc.text("Summary", tableX + colWidth / 2, currentY + 5, { align: "center" });

  const summaryRows = [
    ["Total", total.toFixed(2)],
    ["Discount", discount.toFixed(2)],
    ["Amount Paid", amountPaid.toFixed(2)],
  ];

  let y = currentY + summaryRowHeight + 6;
  doc.setFont("helvetica", "normal");
  summaryRows.forEach(([label, value]) => {
    doc.text(label, tableX + 5, y);
    doc.text(value, tableX + colWidth - 10, y, { align: "right" });
    y += summaryRowHeight;
  });

  const footerY = y + 18;
  doc
    .setFont("helvetica", "bold")
    .text("Thank you!", 105, footerY, { align: "center" });
  doc.setFont("helvetica", "normal").setFontSize(10);
  doc.text(
    "If you have any questions concerning this invoice, contact: support@llb360.com",
    105,
    footerY + 8,
    { align: "center" }
  );
  doc.text(
    "*This is a computer-generated invoice, no signature required.",
    105,
    footerY + 14,
    {
      align: "center",
    }
  );
  doc.text("© 2025 llb360.com", 105, footerY + 20, { align: "center" });

  const pdfBlob = doc.output("blob");
  const url = URL.createObjectURL(pdfBlob);
  window.open(url);
  doc.save(`invoice_${invoice.invoiceNumber}.pdf`);
};

const MyInvoices = () => {
  const { user, token, clearSession } = useAuthStore();
  const [invoices, setInvoices] = useState<InvoiceDetail[]>([]);
  const [loading, setLoading] = useState(true);
  const [downloadingIds, setDownloadingIds] = useState<Set<string>>(new Set());
  const [emailingIds, setEmailingIds] = useState<Set<string>>(new Set());

  useEffect(() => {
    const fetchInvoices = async () => {
      if (!user?.userId) {
        toast.error("User not found.");
        setLoading(false);
        return;
      }
      try {
        const res = await axios.get(
          `${API_BASE_URL}/transaction?userId=${encodeURIComponent(
            user.userId
          )}`,
          {
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
            withCredentials: true,
          }
        );
        let apiData: InvoiceResponseItem[] = [];
        if (Array.isArray(res.data)) apiData = res.data;
        else
          apiData =
            res.data?.success ||
            res.data?.data ||
            res.data?.invoices ||
            res.data?.transactions ||
            [];
        if (apiData.length > 0) {
          const flattened = apiData.flatMap(
            (item) => item.details?.filter((d) => d.invoiceNumber) || []
          );
          setInvoices(flattened);
        } else setInvoices([]);
      } catch (error) {
        if (axios.isAxiosError(error)) {
          const msg =
            error.response?.data?.message || "Failed to fetch invoices";
          if (msg.includes("Session expired") || error.response?.status === 401)
            clearSession("expired");
          else toast.error(msg);
        } else toast.error("Failed to fetch invoices.");
        setInvoices([]);
      } finally {
        setLoading(false);
      }
    };
    fetchInvoices();
  }, [user?.userId, token, clearSession]);

  const handleDownload = async (invoiceDetail: InvoiceDetail) => {
    if (!invoiceDetail?.invoiceNumber) {
      toast.error("Invoice number not found.");
      return;
    }
    if (downloadingIds.has(invoiceDetail.invoiceNumber)) return;
    setDownloadingIds((prev) => new Set(prev).add(invoiceDetail.invoiceNumber));

    try {
      const res = await axios.get(`${API_BASE_URL}/invoice`, {
        params: { invoiceNumber: invoiceDetail.invoiceNumber },
        headers: { Authorization: `Bearer ${token}` },
        withCredentials: true,
      });

      const data: InvoiceApiResponse | InvoiceApiResponse[] =
        res.data?.success || res.data?.data || res.data;
      const invData = Array.isArray(data) ? data[0] : data;

      const subjects = invData.items.map((item) => ({
        name: item.subjectName,
        amount: item.amount,
      }));
      const invoice: Invoice = {
        invoiceNumber: invData.invoiceNumber,
        status: invData.items[0]?.status || "success",
        totalAmount: invData.totalAmount,
        discount: invData.discount,
        subjects,
        date: invData.date,
        userName: invData.userName,
        email: invData.email,
      };

      const doc = new jsPDF();
      generatePdfContent(doc, invoice, user);
    } catch (error) {
      if (axios.isAxiosError(error) && error.response?.status === 404) {
        const fallbackInvoice: Invoice = {
          invoiceNumber: invoiceDetail.invoiceNumber,
          status: invoiceDetail.status || "success",
          totalAmount: invoiceDetail.amount,
          discount: invoiceDetail.discount || 0,
          subjects: (invoiceDetail.subjects || ["Course Subscription"]).map(
            (name) => ({
              name,
              amount:
                (invoiceDetail.amount || 0) /
                (invoiceDetail.subjects?.length || 1),
            })
          ),
          date: invoiceDetail.created,
          userName: user?.fullName,
          email: user?.emailId,
        };
        const doc = new jsPDF();
        generatePdfContent(doc, fallbackInvoice, user);
      } else toast.error("Failed to fetch invoice details.");
    } finally {
      setDownloadingIds((prev) => {
        const newSet = new Set(prev);
        newSet.delete(invoiceDetail.invoiceNumber);
        return newSet;
      });
    }
  };

  const handleSendEmail = async (invoiceDetail: InvoiceDetail) => {
    if (!invoiceDetail?.invoiceNumber) {
      toast.error("Invoice number not found.");
      return;
    }
    if (emailingIds.has(invoiceDetail.invoiceNumber)) return;
    setEmailingIds((prev) => new Set(prev).add(invoiceDetail.invoiceNumber));

    try {
      const res = await axios.post(
        `${API_BASE_URL}/invoice`,
        { invoiceNumber: invoiceDetail.invoiceNumber, sendEmail: true },
        { headers: { Authorization: `Bearer ${token}` }, withCredentials: true }
      );
      toast.success(res.data?.message || "Invoice email sent successfully!");
    } catch (error) {
      if (axios.isAxiosError(error)) {
        const msg =
          error.response?.data?.message || "Failed to send invoice email.";
        toast.error(msg);
      } else {
        toast.error("Failed to send invoice email.");
      }
    } finally {
      setEmailingIds((prev) => {
        const newSet = new Set(prev);
        newSet.delete(invoiceDetail.invoiceNumber);
        return newSet;
      });
    }
  };

  if (loading) return <div>Loading invoices...</div>;

  if (invoices.length === 0)
    return (
      <div>
        No invoices found. You will see invoices here after a successful
        transaction.
      </div>
    );
  return (
    <div className="subjectSection">
      <div className="subjectHeader flex items-center justify-between pb-2 pt-0">
        <h2 className="subjectTitle text-xl font-semibold">My Invoices</h2>
      </div>

      {loading ? (
        <p className="subjectNotFound">Loading...</p>
      ) : invoices.length === 0 ? (
        <p className="subjectNotFound">No invoices found.</p>
      ) : (
        <section className="subjectTableSection">
          <div className="overflow-x-auto">
            <div className="max-h-[calc(100vh-20rem)] overflow-y-auto">
              <table className="min-w-full border-collapse">
                <thead className="sticky top-0 bg-white z-10">
                  <tr className="tableHeadRow">
                    {tableHeaders.map((header) => (
                      <th key={header} className="tableHeadValue">
                        {header}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {invoices.map((inv, index) => {
                    const isBusy = downloadingIds.has(inv.invoiceNumber);
                    return (
                      <tr
                        key={`${inv.invoiceNumber}-${index}`}
                        className="tableBodyRow"
                      >
                        <td className="tableBodyValue">{inv.invoiceNumber}</td>
                        <td className="tableBodyValue">
                          {new Date(inv.created).toLocaleDateString("en-GB")}
                        </td>
                        <td className="tableBodyValue">
                          {inv.status?.toLowerCase() === "success" ? (
                            <span className="px-4 py-1 rounded-full bg-green-200 text-green-700 font-semibold">
                              Success
                            </span>
                          ) : (
                            <span className="px-4 py-1 rounded-full bg-gray-200 text-gray-700 font-semibold">
                              {inv.status || "Unknown"}
                            </span>
                          )}
                        </td>
                        <td className="tableBodyValue">
                          ₹{inv.amount.toFixed(2)}
                        </td>
                        <td className="tableBodyValue flex gap-2">
                          <button
                            onClick={() => handleDownload(inv)}
                            disabled={isBusy}
                            className={`flex items-center gap-2 px-3 py-1 rounded-full transition cursor-pointer ${
                              isBusy
                                ? "bg-gray-400 cursor-not-allowed"
                                : "bg-myLabelText text-white hover:bg-myButtonBg"
                            }`}
                          >
                            <FaFileDownload size={16} />
                          </button>

                          <button
                            onClick={() => handleSendEmail(inv)}
                            disabled={emailingIds.has(inv.invoiceNumber)}
                            className={`flex items-center gap-2 px-3 py-1 rounded-full transition cursor-pointer ${
                              emailingIds.has(inv.invoiceNumber)
                                ? "bg-gray-400 cursor-not-allowed"
                                : "bg-myLabelText text-white hover:bg-myButtonBg"
                            }`}
                          >
                            <MdAttachEmail size={16} />
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </section>
      )}
    </div>
  );
};

export default MyInvoices;
